import { useCallback, useEffect, useState } from "react";

export type ThemeKind = "light" | "dark" | "system";
const KEY = "emlx:theme";

function systemDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  );
}

function apply(kind: ThemeKind): void {
  const dark = kind === "dark" || (kind === "system" && systemDark());
  document.body.setAttribute("data-theme", dark ? "dark" : "light");
}

/** Minimal theme controller for the NEXT UI: cycles light → dark → system. */
export function useTheme(): { kind: ThemeKind; cycle: () => void } {
  const [kind, setKind] = useState<ThemeKind>(() => {
    if (typeof window === "undefined") return "system";
    // Guarded like every other storage READ in the app. Chrome throws
    // SecurityError from localStorage.getItem when site data is blocked, and
    // this runs during the first render of the shell: unguarded, a privacy
    // setting the reader chose took the whole app down before it painted.
    // The write below was already wrapped; the read was not.
    try {
      const stored = localStorage.getItem(KEY) as ThemeKind | null;
      return stored === "light" || stored === "dark" || stored === "system"
        ? stored
        : "system";
    } catch {
      return "system";
    }
  });

  useEffect(() => {
    apply(kind);
    try {
      localStorage.setItem(KEY, kind);
    } catch {
      /* private mode, ignore */
    }
    if (kind !== "system") return;
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = () => apply("system");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [kind]);

  const cycle = useCallback(() => {
    setKind((k) => (k === "light" ? "dark" : k === "dark" ? "system" : "light"));
  }, []);

  return { kind, cycle };
}
